import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ViewEncapsulation,
  computed,
  forwardRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router } from '@angular/router';

import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';

import { TranslocoModule } from '@jsverse/transloco';

import { ButtonUtilityDirective } from '../directives/index';
import { NbxMenuItem } from '../shared/interfaces/menu-item.interface';

@Component({
  selector: 'nbx-menu-button',
  standalone: true,
  imports: [ButtonModule, MenuModule, TranslocoModule, TooltipModule, ButtonUtilityDirective],
  templateUrl: './menu-button.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MenuButtonComponent),
      multi: true,
    },
  ],
})
export class MenuButtonComponent implements ControlValueAccessor {
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _router = inject(Router);

  public menuItems = input<NbxMenuItem[]>([]);
  public icon = input<string>('pi pi-globe');
  public tooltip = input<string>('');
  public badge = input<number | undefined>(undefined);
  public styleClass = input<string>('');
  public appendTo = input<'body' | 'self' | HTMLElement | null | undefined>('body');

  public selectedItem = signal<NbxMenuItem | null>(null);

  public disabled = false;

  public readonly primeMenuItems = computed<MenuItem[]>(() =>
    this.menuItems().map((item: NbxMenuItem) => ({
      id: item.value,
      label: item.label,
      icon: item.icon,
      disabled: item.disabled,
      separator: item.separator,
      command: () => this.onItemSelect(item),
    }))
  );

  public onItemSelect(item: NbxMenuItem): void {
    if (this.disabled) {
      return;
    }
    this.selectedItem.set(item);
    this._onChange(item.value);
    this._onTouched();

    if (item.url) {
      this._router.navigateByUrl(item.url);
    }
  }

  writeValue(value: string | null): void {
    const item = this.menuItems().find((i: NbxMenuItem) => i.value === value) ?? null;
    this.selectedItem.set(item);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._cdr.markForCheck();
  }

  private _onChange: (value: string | null) => void = () => undefined;
  private _onTouched: () => void = () => undefined;
}
